<template>
  <div class="staff-transactions-page" data-testid="staff-transactions-page">
    <page-header
      :title="t('Transações')"
      icon="pi pi-credit-card"
    />

    <StaffTransactionsTable
      :rows="displayRows"
      data-testid="staff-transactions-table-wrapper"
      @refresh="handleRefresh"
    />

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useAdminTransactions } from '../../../composables/useAdminTransactions'
import { useFiltro } from '../../../composables/useFiltro'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import PageHeader from '../../../ui/PageHeader.vue'
import StaffTransactionsTable from './StaffTransactionsTable.vue'
import type { AttemptDisplayRow } from './StaffTransactionsTable.vue'
import type { Subscriptions_AdminTransactionRow } from '../../../contracts/Subscriptions_AdminTransactionRow'

const { t, n } = useI18n()
const { transactionRows, listTransactions } = useAdminTransactions()
const { selectedFranchises } = useFiltro()
const { fromApiDateTimeFormat, formatDateTime } = useDateFormatter()

type SortableRow = AttemptDisplayRow & { _sort_ts: string }

// Flatten each transaction into one row per attempt.
// If a transaction has no attempts, it is emitted as a single row itself.
// Sort newest-first by the row's date (attempt created_at or tx created_at).
function flattenToAttemptRows(txs: Subscriptions_AdminTransactionRow[]): AttemptDisplayRow[] {
  const rows: SortableRow[] = []

  for (const tx of txs) {
    const attempts = tx.attempts ?? []

    const txCurrency = tx.currency ?? 'BRL'
    const txAmountMajor = (tx.amount_cents ?? 0) / currencyFactor('pt-BR', txCurrency)
    const txAmountDisplay = n(txAmountMajor, { style: 'currency', currency: txCurrency })

    if (attempts.length === 0) {
      rows.push({
        id: tx.id,
        franquia_id: tx.franquia_id,
        franchise_name: tx.franchise_name,
        kind: tx.kind,
        currency: txCurrency,
        amount_brl: txAmountMajor,
        amount_display: txAmountDisplay,
        provider: tx.provider,
        provider_charge_id: tx.provider_charge_id,
        status: tx.status,
        created_at_display: tx.created_at
          ? formatDateTime(fromApiDateTimeFormat(tx.created_at))
          : '—',
        paid_at_display: tx.paid_at
          ? formatDateTime(fromApiDateTimeFormat(tx.paid_at))
          : '—',
        _row_key: `${tx.id}-tx`,
        _sort_ts: tx.created_at ?? '',
      })
    } else {
      for (let i = 0; i < attempts.length; i++) {
        const attempt = attempts[i]
        // M3: money is a property of the CHARGE, never the attempt — it may
        // appear on at most one display row per charge, the TERMINAL attempt
        // (the last one, which carries the verdict). Earlier attempts are a
        // timeline entry only; summing the column must not double-count a
        // confirmed charge across its pending + confirmed attempt rows.
        const isTerminal = i === attempts.length - 1
        // S9-04: attempt.status is the event-INGESTION vocabulary for
        // subscription-linked rows (subscription_events.status — pending/
        // processed/failed/dead), never the payment-outcome vocabulary the
        // Situação badge map understands (pending/confirmed/failed/overdue/
        // expired) — rendering it raw leaked English ingestion codes (e.g.
        // "processed") onto a charge that was actually overdue/confirmed.
        // The CHARGE's resolved status (tx.status, already correct — see
        // resolveDisplayStatus() on the backend) belongs on the SAME row
        // that carries the money: the terminal attempt. Earlier attempts
        // show no payment status, same treatment as their blanked amount.
        rows.push({
          id: tx.id,
          franquia_id: tx.franquia_id,
          franchise_name: tx.franchise_name,
          kind: tx.kind,
          currency: txCurrency,
          amount_brl: isTerminal ? txAmountMajor : undefined,
          amount_display: isTerminal ? txAmountDisplay : '—',
          provider: tx.provider,
          provider_charge_id: tx.provider_charge_id,
          status: isTerminal ? tx.status : undefined,
          created_at_display: attempt.created_at
            ? formatDateTime(fromApiDateTimeFormat(attempt.created_at))
            : '—',
          // S9-B: "Pago em" pertence à COBRANÇA e mora na linha terminal, junto
          // do dinheiro e do status. Antes o gate era `attempt.status ===
          // 'confirmed'`, que nunca é verdade para kind='cycle'/'setup_fee' —
          // ali o vocabulário é o de INGESTÃO (pending/processed/failed/dead),
          // não o de pagamento — então a coluna ficava SEMPRE vazia justamente
          // nas cobranças de assinatura. O dado certo é o próprio paid_at da
          // cobrança: se ele existe, o pagamento aconteceu.
          paid_at_display: isTerminal && tx.paid_at
            ? formatDateTime(fromApiDateTimeFormat(tx.paid_at))
            : '—',
          _row_key: `${tx.id}-${i}`,
          _sort_ts: attempt.created_at ?? tx.created_at ?? '',
        })
      }
    }
  }

  // Sort newest-first
  rows.sort((a, b) => (a._sort_ts < b._sort_ts ? 1 : a._sort_ts > b._sort_ts ? -1 : 0))

  return rows
}

const displayRows = computed<AttemptDisplayRow[]>(() => {
  let txs = transactionRows.value

  // Global filter: restrict to selected franchises when the selection is non-empty.
  const franchises = selectedFranchises.value as Array<{ id: number | string }>
  if (franchises.length > 0) {
    const ids = new Set(franchises.map((f) => Number(f.id)))
    txs = txs.filter((tx) => ids.has(tx.franquia_id))
  }

  return flattenToAttemptRows(txs)
})

async function handleRefresh(): Promise<void> {
  try {
    await listTransactions()
  } catch (err) {
    log('StaffTransactionsPage', 'handleRefresh', 'Erro ao recarregar transações', err)
  }
}

onMounted(async () => {
  // M3c (D5): after a staff member marks a charge as estornada in the detail
  // modal, the table still shows 'Estorno pendente' until it re-reads. Refresh
  // from the server rather than patching the row locally — the console is a
  // projection of what the database says, never of what the click assumed.
  eventBus.on('staffTransaction.saved', handleRefresh)

  try {
    await listTransactions()
  } catch (err) {
    log('StaffTransactionsPage', 'onMounted', 'Erro ao carregar transações', err)
  }
})

onBeforeUnmount(() => {
  eventBus.off('staffTransaction.saved', handleRefresh)
})
</script>

<style scoped>
.staff-transactions-page {
  padding: 24px;
}
</style>
